import { GoogleReCaptchaProvider } from '@google-recaptcha/react'
import AddIcon from '@mui/icons-material/Add'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import CloseIcon from '@mui/icons-material/Close'
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  Divider,
  IconButton,
  IconButtonProps,
  Link,
  Stack,
  styled,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { FC, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToggle } from 'react-use'

import { DifficultyIcon } from '../'
import { Difficulties, StatusDescriptions, StatusEnum, StatusLabels, Trail, TrailSystem } from '../../models'
import { TrailMapResponse } from '../../models/TrailMapResponse.model'
import { AppConfigProvider } from '../../providers'
import { ApiQueryService } from '../../services'
import { getSponsorHref } from '../../utils'
import ReportIssueForm from '../ReportIssueForm/ReportIssueForm'
import { useGetObstructionType } from '../ReportIssueForm/useObstructionReportForm'
import { TrailMap } from '../TrailMap/TrailMap'

const TrailDetailDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'hasFeaturedImage' && prop !== 'hasGeometry' && prop !== 'isTrailMapLoading',
})<
  DialogProps & {
    hasFeaturedImage: boolean
    hasGeometry: boolean
    isTrailMapLoading: boolean
  }
>(({ theme, hasFeaturedImage, hasGeometry, isTrailMapLoading }) => {
  // Default to full width while trail map data is loading to avoid a jarring
  // resize once geometry arrives (same race condition as shouldHideTrailMap).
  const shouldUseSmallWidth = !isTrailMapLoading && !hasFeaturedImage && !hasGeometry

  return {
    '& .MuiDialog-paper': {
      background: theme.palette.bright.main,
      borderRadius: theme.spacing(1.5),
      color: theme.palette.bright.contrastText,
      height: '70vh',
      maxHeight: '95%',
      maxWidth: shouldUseSmallWidth ? 500 : 950,
      width: '95%',
      [theme.breakpoints.down('desktop')]: {
        maxWidth: 500,
      },
      [theme.breakpoints.down('sm')]: {
        height: '95%',
        maxWidth: '100%',
        width: '90%',
      },
      [theme.breakpoints.down('tiny')]: {
        height: '100%',
        margin: 0,
        maxHeight: '100%',
        maxWidth: '100%',
        width: '100%',
      },
    },
  }
})

const TrailDetailDialogTitle = styled(DialogTitle)({
  paddingBottom: 0,
})

const TrailDetailContent = styled(DialogContent)<DialogContentProps>({
  '& .MuiTypography-root': {
    letterSpacing: 0,
  },
  /* width */
  '&::-webkit-scrollbar': {
    height: '16px',
    width: '16px',
  },
  /* Handle */
  '&::-webkit-scrollbar-thumb': {
    background: '#cbd5e0',
    border: '3px solid #edf2f7',
    borderRadius: '100vh',
  },
  /* Handle on hover */
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a0aec0',
  },
  /* Track */
  '&::-webkit-scrollbar-track': {
    background: '#edf2f7',
    borderRadius: '100vh',
  },
  // left: '50%',
  overflowY: 'auto',
  // padding: theme.spacing(3),
  // paddingBottom: theme.spacing(2),
  // position: 'absolute',
  // top: '50%',
  // transform: 'translate(-50%, -50%)',
})

const CloseButton = styled(IconButton)<IconButtonProps>(({ theme }) => ({
  color: theme.palette.grey[500],
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
}))

const ParkingIconCircle = styled(LocalParkingIcon)({
  border: '1px solid',
  borderRadius: '50%',
  height: 20,
  padding: 4,
  width: 20,
})

const StatusLabel = styled(Typography)<TypographyProps>({
  fontSize: 15,
  fontWeight: 600,
  left: '50%',
  lineHeight: '1.1',
  maxWidth: '90px', // Constrain width to fit within circle
  padding: '0 10px',
  position: 'absolute',
  textAlign: 'center',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  whiteSpace: 'normal',
})

const TrailDetailValue = styled(Typography)<TypographyProps>({
  fontSize: 18,
  fontWeight: 700,
})

const TrailDetailLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
  color: theme.palette.grey[600],
}))

const CollapseButton = styled(Button)<ButtonProps>({
  fontSize: 18,
  fontWeight: 700,
  justifyContent: 'start',
  letterSpacing: 0,
  textTransform: 'none',
})

const FeatureList = styled('ul')(({ theme }) => ({
  listStyleType: 'none',
  margin: 0,
  paddingLeft: theme.spacing(3.5),
}))

// const DataPartnerLogo = styled('img')(({ theme }) => ({
//   maxHeight: 16,
//   maxWidth: '100%',
//   [theme.breakpoints.down('sm')]: {
//     maxHeight: 12,
//   },
// }))

type TrailDetailProps = {
  trail: Trail
  trailSystem: TrailSystem
  refetchData: () => void
}

const google_site_key = AppConfigProvider.googleRecaptchaSiteKey

export const TrailDetail: FC<TrailDetailProps> = (props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const [showFeatures, toggleShowFeatures] = useToggle(false)
  const [showReportIssue, toggleShowReportIssue] = useToggle(false)
  const [showDescription, toggleShowDescription] = useToggle(false)
  const sponsor = props.trail?.sponsor
  console.log(sponsor)

  const { data: obstructionTypes } = useGetObstructionType()

  // Query to get trail map data to check for geometry
  const trailMapQuery = useQuery<TrailMapResponse>({
    enabled: !!props.trail.id,
    queryFn: async () => {
      const res = await new ApiQueryService().get<TrailMapResponse>({
        endpoint: `trail-map/${props.trail.id}`,
      })
      return res
    },
    queryKey: ['trail-map', props.trail.id],
  })

  // Check if geometry data is available
  const hasGeometry = useMemo(() => {
    const geometry = trailMapQuery.data?.geometry
    return !!(geometry && geometry.features && geometry.features.length > 0)
  }, [trailMapQuery.data?.geometry])

  // Check if featured image is available
  const hasFeaturedImage = useMemo(() => {
    return !!props.trail.featuredImgUrl?.length
  }, [props.trail.featuredImgUrl])

  // Determine if we should hide the trail map section
  // Keep the section visible while the trail map query is loading so the
  // TrailMap component mounts and the API call can complete. Without this,
  // hasFeaturedImage and hasGeometry are both false on initial render,
  // hiding the section before data has a chance to load.
  const shouldHideTrailMap = useMemo(() => {
    if (trailMapQuery.isLoading) return false
    return !hasFeaturedImage && !hasGeometry
  }, [trailMapQuery.isLoading, hasFeaturedImage, hasGeometry])

  const isFreezeThaw = useMemo(
    () =>
      props.trail.status?.status !== 0 &&
      props.trail.status?.freezeThawStatus?.status &&
      props.trail.status?.freezeThawStatus?.status > 0,
    [props.trail.status?.status, props.trail.status?.freezeThawStatus?.status],
  )

  // Resolve parking coordinates atomically from a single source. If the
  // linked parking lot has both lat and long, use them together; otherwise
  // fall back entirely to the trail's inline coords. Never mix axes.
  const parkingCoords = useMemo(() => {
    const lot = props.trail.parkingLot
    if (lot && lot.gpsLat != null && lot.gpsLong != null) {
      return { lat: lot.gpsLat, long: lot.gpsLong }
    }
    return { lat: props.trail.parkingGpsLat, long: props.trail.parkingGpsLong }
  }, [props.trail.parkingLot, props.trail.parkingGpsLat, props.trail.parkingGpsLong])

  const handleClose = useCallback(() => {
    navigate(`/trail-system/${props.trailSystem.permalink}${location.search}`)
  }, [props.trailSystem])
  return (
    <TrailDetailDialog
      hasFeaturedImage={hasFeaturedImage}
      hasGeometry={hasGeometry}
      isTrailMapLoading={trailMapQuery.isLoading}
      aria-labelledby="trail-detail-name"
      open={true}
      onClose={handleClose}
    >
      <TrailDetailDialogTitle id="trail-detail-name">
        <Stack direction="row" justifyContent="start" alignItems="center" spacing={2}>
          <Typography component="span" fontSize={30} fontWeight={700} sx={{ lineHeight: 1.2, paddingBottom: 1.2 }}>
            {props.trail.name}
          </Typography>
          <Box paddingTop={props.trail.difficultyId !== Difficulties['Double Black'] ? 1 : 0}>
            <DifficultyIcon difficultyId={props.trail.difficultyId} fontSize={20} />
          </Box>
        </Stack>
        <CloseButton color="secondary" size="small" onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </TrailDetailDialogTitle>
      <TrailDetailContent>
        <Stack
          direction={{ desktop: 'row', xs: 'column' }}
          justifyContent={{ desktop: 'space-between', xs: 'start' }}
          alignItems="start"
          spacing={2}
        >
          <Stack width={{ desktop: !shouldHideTrailMap ? '50%' : '100%', xs: '100%' }} spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="start">
              <Box>
                <Typography component="h3" variant="body2">
                  {props.trailSystem.name}
                </Typography>
                <Typography component="p" variant="body2">
                  {Math.round(props.trail.distance * 10) / 10} miles
                </Typography>
                <Button
                  startIcon={<ParkingIconCircle />}
                  variant="outlined"
                  href={`https://www.google.com/maps/search/?api=1&query=${parkingCoords.lat},${parkingCoords.long}`}
                  target="_blank"
                  color="inherit"
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginLeft: 0,
                      marginRight: '6px',
                    },
                    borderRadius: '20px',
                    fontSize: '12px',
                    ml: -1,
                    mt: 2,
                    pb: 0.5,
                    pl: 0.75,
                    pr: 0.75,
                    pt: 0.5,
                  }}
                >
                  Parking
                </Button>
              </Box>
              {isFreezeThaw ? (
                <Tooltip
                  title={
                    'Soil temps are fluctuating above and below the freezing point, leading to variable conditions. Trails may be firm and rideable under 30° F.'
                  }
                >
                  <Box width={{ desktop: '50%' }}>
                    <Stack justifyContent="center" alignItems="center" position="relative">
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={95}
                        thickness={5}
                        sx={{ color: theme.palette.freezeThaw.main }}
                      />
                      <StatusLabel component="p" variant="h6">
                        Freeze Thaw
                      </StatusLabel>
                    </Stack>
                  </Box>
                </Tooltip>
              ) : (
                <Box width={{ desktop: '50%' }}>
                  <Stack justifyContent="center" alignItems="center" position="relative">
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={95}
                      thickness={5}
                      sx={{
                        color: {
                          [-1]: theme.palette.grey[600],
                          0: theme.palette.grey[300],
                          1: theme.palette.trailStatus.dark,
                          2: theme.palette.trailStatus.main,
                          3: theme.palette.trailStatus.light,
                        }[props.trail.status?.status ?? -1],
                      }}
                    />
                    <StatusLabel component="p" variant="h6">
                      {StatusLabels[props.trail.status?.status ?? StatusEnum.Unknown]}
                    </StatusLabel>
                  </Stack>
                </Box>
              )}
            </Stack>
            {props.trail.status?.status === 0 && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <DoDisturbOnIcon />
                <Typography component="p" variant="body2">
                  {props.trail.manualTrailStatuses?.[0].reason || StatusDescriptions[0]}
                </Typography>
              </Stack>
            )}
            {props.trail.status?.status === -1 && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <BrokenImageIcon />
                <Typography component="p" variant="body2">
                  {StatusDescriptions[-1]}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={{ tiny: 4, xs: 2 }}>
              <Stack>
                {!!props.trail.surfaceType?.length && (
                  <>
                    <TrailDetailValue>{props.trail.surfaceType}</TrailDetailValue>
                    <TrailDetailLabel variant="caption">Surface Type</TrailDetailLabel>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                <Stack>
                  <TrailDetailValue>{props.trail.climb || 0} ft</TrailDetailValue>
                  <TrailDetailLabel variant="caption">Climb</TrailDetailLabel>
                </Stack>
                <Stack>
                  <TrailDetailValue>{props.trail.descent || 0} ft</TrailDetailValue>
                  <TrailDetailLabel variant="caption">Descent</TrailDetailLabel>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="column" gap={1} style={{ marginBottom: 10 }}>
              {Array.from(
                (props.trail.obstructionReports ?? [])
                  .filter((report) => report.obstructionType?.name !== 'Other')
                  .reduce((acc, report) => {
                    const name = report.obstructionType?.name
                    if (!name) return acc
                    const existing = acc.get(name)
                    if (existing) {
                      existing.count++
                    } else {
                      acc.set(name, { count: 1, name, symbol: report.obstructionType?.symbol })
                    }
                    return acc
                  }, new Map<string, { symbol: string | undefined; name: string; count: number }>())
                  .values(),
              ).map((group) => (
                <Stack direction="row" key={group.name} gap={1} alignItems="center">
                  <Typography>{group.symbol}</Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {group.name}
                    {group.count > 1 ? ` (${group.count})` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {!!props.trail.description?.length && (
              <Stack style={{ marginTop: 0 }}>
                <CollapseButton
                  onClick={toggleShowDescription}
                  startIcon={showDescription ? <RemoveIcon /> : <AddIcon />}
                  variant="text"
                  color="inherit"
                  style={{ paddingLeft: 0 }}
                >
                  About
                </CollapseButton>
                <Collapse in={showDescription}>
                  <Typography
                    component="p"
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: props.trail.description }}
                  ></Typography>
                </Collapse>
              </Stack>
            )}

            <Divider color="gray" style={{ marginBottom: 0, marginTop: 0 }} />
            <Stack style={{ marginTop: 0 }}>
              <CollapseButton
                onClick={toggleShowReportIssue}
                startIcon={showReportIssue ? <RemoveIcon /> : <AddIcon />}
                variant="text"
                color="inherit"
                style={{ paddingLeft: 0 }}
              >
                Report an issue
              </CollapseButton>
              <Collapse in={showReportIssue}>
                <GoogleReCaptchaProvider type="v3" siteKey={google_site_key ?? ''}>
                  <ReportIssueForm
                    toggleShowReportIssue={toggleShowReportIssue}
                    trail={props.trail}
                    refetchData={props.refetchData}
                    obstructionTypes={obstructionTypes}
                    hasTrailMap={hasGeometry}
                  />
                </GoogleReCaptchaProvider>
              </Collapse>
            </Stack>
            <Divider color="gray" style={{ marginBottom: 0, marginTop: 0 }} />

            {!!props.trail.features?.length && (
              <>
                <Stack style={{ marginTop: 0 }}>
                  <CollapseButton
                    onClick={toggleShowFeatures}
                    startIcon={showFeatures ? <RemoveIcon /> : <AddIcon />}
                    variant="text"
                    color="inherit"
                    style={{ paddingLeft: 0 }}
                  >
                    Features
                  </CollapseButton>
                  <Collapse in={showFeatures}>
                    <FeatureList>
                      {props.trail.features.map((feature, i) => (
                        <Typography component="li" key={i} fontWeight={500}>
                          {feature.name}
                        </Typography>
                      ))}
                    </FeatureList>
                  </Collapse>
                </Stack>
                {!!props.trail.features?.length && <Divider color="gray" style={{ marginBottom: 0, marginTop: 0 }} />}
              </>
            )}
          </Stack>
          {!shouldHideTrailMap && (
            <Stack
              width={{ desktop: '60%', xs: '100%' }}
              justifyContent="center"
              alignItems="center"
              sx={{
                boxSizing: 'border-box',
                minWidth: { desktop: 400, xs: 'auto' }, // Only apply minWidth on desktop
                overflow: 'hidden', // Prevent map from overflowing
                position: 'relative',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" mb={2}>
                <Typography
                  variant="h6"
                  component="h3"
                  fontSize={18}
                  fontWeight={700}
                  style={{ letterSpacing: 0, paddingLeft: 0, textTransform: 'none' }}
                >
                  Trail Map
                </Typography>
              </Stack>
              <Box
                sx={{
                  borderRadius: 1,
                  height: '100%',
                  overflow: 'hidden', // Contain the map
                  position: 'relative',
                  width: '100%',
                }}
              >
                <TrailMap trailId={props.trail.id} trail={props.trail} />
              </Box>
            </Stack>
          )}
        </Stack>
      </TrailDetailContent>
      <DialogActions>
        <Stack
          direction={{ sm: 'row', xs: 'column' }}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          paddingY={{ sm: 1, xs: 0.5 }}
          paddingX={{ sm: 2, xs: 2 }}
          spacing={{ sm: 0, xs: 1 }}
          sx={{ position: 'relative' }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* <Typography fontSize={{ sm: 12, xs: 10 }}>Data partner:</Typography>
            <Link href="https://wearetrailblazers.org/" target="_blank" rel="noreferrer">
              <Stack>
                <DataPartnerLogo
                  src="https://wp-flowfeed.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/02/26214834/Trailblazers-Logo_Black.png"
                  alt="Trailblazers"
                />
              </Stack>
            </Link> */}
          </Stack>
          <Box sx={{ flex: { sm: 1 }, width: { sm: 'auto', xs: 0 } }} />
          {sponsor && (
            <Typography
              variant="body2"
              fontSize={{ sm: 16, xs: 14 }}
              fontWeight={600}
              sx={{
                opacity: 0.8,
                order: { sm: 'unset', xs: 1 },
                textAlign: { sm: 'left', xs: 'center' },
                [theme.breakpoints.up('sm')]: {
                  left: '50%',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                },
              }}
            >
              Powered by{' '}
              {sponsor.url ? (
                <Link
                  href={getSponsorHref(sponsor.url)}
                  target="_blank"
                  rel="noreferrer"
                  color="inherit"
                  sx={{
                    '&:hover': { opacity: 0.8 },
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  {sponsor.name}
                </Link>
              ) : (
                <span style={{ fontWeight: 600 }}>{sponsor.name}</span>
              )}
            </Typography>
          )}

          <Link
            href="https://www.flowfeed.app/trail-data-feedback/"
            target="_blank"
            rel="noreferrer"
            color="inherit"
            sx={{
              display: 'flex',
              flex: { sm: 1 },
              justifyContent: { sm: 'flex-end' },
              order: { sm: 'unset', xs: 2 },
            }}
          >
            <Typography variant="body2" fontSize={{ sm: 14, xs: 12 }}>
              Provide feedback
            </Typography>
          </Link>
        </Stack>
      </DialogActions>
    </TrailDetailDialog>
  )
}
TrailDetail.displayName = 'TrailDetail'
